/**
 * FormDialog
 *
 * The form dialog shell shared by the leave and arrêt de travail forms, in the look of the
 * permissions page's "Add profile" dialog (ProfileModal): a soft gradient header with the
 * title in navy, one row per field with its label on the left, inputs with a faint fill and
 * an orange focus ring, and a centred footer led by the green save button.
 *
 * ProfileModal is PrimeReact; this is the same design in MUI, which new components use.
 * Presentational only — the caller owns the state and what the buttons do.
 */
import React from "react";
import { Box, Button, Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const NAVY = "rgb(0, 40, 128)";
const EASE = "200ms cubic-bezier(0.4, 0, 0.2, 1)";

/** Inputs as ProfileModal draws them: rounded, faintly filled, orange ring on focus. */
export const formFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "rgba(0, 40, 128, 0.02)",
    fontSize: "0.9rem",
    transition: `box-shadow ${EASE}, background-color ${EASE}`,
    "& fieldset": { borderWidth: "1.5px", borderColor: "rgba(0, 40, 128, 0.1)" },
    "&:hover fieldset": { borderColor: "rgba(0, 40, 128, 0.25)" },
    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(255, 144, 21, 0.12)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(255, 144, 21, 0.5)", borderWidth: "1.5px" },
  },
};

const buttonBaseSx = {
  borderRadius: "12px",
  px: 3.5,
  py: 1.1,
  fontWeight: 600,
  fontSize: "0.88rem",
  letterSpacing: "0.01em",
  textTransform: "none",
  minWidth: 120,
  transition: `transform 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), background ${EASE}`,
  "&:active": { transform: "scale(0.98)" },
};

const BUTTON_VARIANTS = {
  // ProfileModal's save button.
  primary: {
    ...buttonBaseSx,
    color: "#fff",
    background: "linear-gradient(135deg, rgb(58, 156, 96) 0%, rgb(76, 175, 112) 100%)",
    boxShadow: "0 2px 8px rgba(58, 156, 96, 0.25), 0 1px 3px rgba(58, 156, 96, 0.15)",
    "&:hover": {
      background: "linear-gradient(135deg, rgb(48, 140, 82) 0%, rgb(62, 158, 96) 100%)",
      boxShadow: "0 4px 16px rgba(58, 156, 96, 0.35), 0 2px 6px rgba(58, 156, 96, 0.2)",
      transform: "translateY(-1px)",
    },
    "&.Mui-disabled": { background: "rgba(0, 40, 128, 0.08)", color: "rgba(0, 40, 128, 0.3)", boxShadow: "none" },
  },
  secondary: {
    ...buttonBaseSx,
    color: NAVY,
    border: "1.5px solid rgba(0, 40, 128, 0.2)",
    bgcolor: "transparent",
    "&:hover": { bgcolor: "rgba(0, 40, 128, 0.05)", borderColor: "rgba(0, 40, 128, 0.4)" },
  },
  danger: {
    ...buttonBaseSx,
    color: "#C62828",
    border: "1.5px solid rgba(198, 40, 40, 0.45)",
    bgcolor: "transparent",
    "&:hover": { bgcolor: "rgba(198, 40, 40, 0.06)", borderColor: "#C62828", transform: "translateY(-1px)" },
  },
};

/**
 * A footer button.
 * @param {"primary"|"secondary"|"danger"} props.variant
 */
export function FormButton({ variant = "primary", sx, ...props }) {
  return <Button disableElevation {...props} sx={{ ...BUTTON_VARIANTS[variant], ...sx }} />;
}

/**
 * One field: its label on the left, the input on the right (stacked on a phone).
 * @param {React.ReactNode} props.label
 * @param {boolean} [props.required] - marks the label
 * @param {React.ReactNode} [props.help] - small text under the input
 * @param {boolean} [props.alignTop] - for tall inputs (lists, text areas): label at the top
 */
export function FormRow({ label, required, help, alignTop, children }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 3fr) minmax(0, 9fr)" },
        columnGap: 2,
        rowGap: 0.5,
        alignItems: alignTop ? "start" : "center",
        px: 1,
        py: 1.25,
        borderRadius: "12px",
        transition: `background-color ${EASE}`,
        "&:hover": { bgcolor: "rgba(0, 40, 128, 0.02)" },
      }}
    >
      <Box
        sx={{
          fontWeight: 600,
          fontSize: "0.9rem",
          color: NAVY,
          letterSpacing: "-0.01em",
          display: "flex",
          alignItems: "center",
          minHeight: { sm: 40 },
        }}
      >
        {label}
        {required && <Box component="span" sx={{ color: "#FF9015", ml: 0.5 }}>*</Box>}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {children}
        {help && (
          <Typography component="small" sx={{ color: "rgba(0, 40, 128, 0.5)", fontSize: "0.8rem", mt: 0.75, display: "block" }}>
            {help}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** A heading between groups of rows. */
export function FormSection({ children }) {
  return (
    <Typography
      sx={{
        mt: 2,
        mb: 0.5,
        px: 1,
        pt: 1.5,
        borderTop: "1px solid rgba(0, 40, 128, 0.08)",
        fontSize: "0.75rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "rgba(0, 40, 128, 0.55)",
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.titleAdornment] - beside the title (a status chip)
 * @param {React.ReactNode} [props.actions] - the footer buttons; no footer when omitted
 * @param {string} [props.maxWidth] - MUI Dialog maxWidth
 * @param {boolean} [props.fullScreen] - on a phone
 */
export default function FormDialog({ open, onClose, title, titleAdornment, actions, maxWidth = "sm", fullScreen, children }) {
  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      fullScreen={fullScreen}
      PaperProps={{
        sx: fullScreen ? {} : {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0, 40, 128, 0.12), 0 2px 8px rgba(0, 40, 128, 0.06)",
          border: "1px solid rgba(0, 40, 128, 0.06)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 2.5,
          background: "linear-gradient(135deg, #fff5eb, #f0f2f8)",
          borderBottom: "1px solid rgba(0, 40, 128, 0.1)",
        }}
      >
        <Typography component="h2" sx={{ fontWeight: 600, color: NAVY, fontSize: "1.1rem", letterSpacing: "-0.02em", flex: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {titleAdornment}
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(0, 40, 128, 0.6)" }}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2, bgcolor: "#fff" }}>
        {children}
      </DialogContent>
      {actions && (
        <Box
          sx={{
            display: "flex",
            // Left to right, not centred as a block. A centred pair floats with empty space
            // either side and lines up with nothing else in the dialog; hard left puts the
            // actions where the eye already is after reading the form. On a phone they go
            // full width instead, where alignment is moot and reach is what matters.
            justifyContent: "flex-start",
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            flexWrap: "wrap",
            gap: 1.5,
            px: 3,
            pt: 1.5,
            pb: 2.5,
            bgcolor: "#f8f9fc",
            borderTop: "1px solid rgba(0, 40, 128, 0.08)",
          }}
        >
          {actions}
        </Box>
      )}
    </Dialog>
  );
}
